
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, User, Car, MapPin, Calendar as CalendarIcon, CreditCard, Check, Sparkles, ClipboardList, Plus, Trash2, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { getVehicleMakes, getVehicleYears, getVehicleColors } from '../lib/api';
import { cleaningApi } from '../lib/cleaningApi';
import { MobileBookingActionBar } from '../components/MobileBookingActionBar';

// Import Steps
import ServiceStep from '../components/cleaning/steps/ServiceStep';
import DetailsStep from '../components/cleaning/steps/DetailsStep';
import LocationStep from '../components/cleaning/steps/LocationStep';
import ScheduleStep from '../components/cleaning/steps/ScheduleStep';
import ReviewStep from '../components/cleaning/steps/ReviewStep';
import PaymentStep from '../components/cleaning/steps/PaymentStep';

const LOGO_URL = "/pt-logo.png?v=2";
const WHATSAPP_LINK = "https://wa.me/message/RYIKLUKDZT2NC1";
const AUTHORIZED_CORPORATE_IDS = new Set(['111377', '783251']);

const STEPS = [
  { id: 1, name: 'Service', icon: Sparkles },
  { id: 2, name: 'Details & Vehicle', icon: User },
  { id: 3, name: 'Location', icon: MapPin },
  { id: 4, name: 'Schedule', icon: CalendarIcon },
  { id: 5, name: 'Review', icon: ClipboardList },
  { id: 6, name: 'Payment', icon: CreditCard },
];

const REFERRAL_OPTIONS = [
  "WhatsApp Status",
  "Word of Mouth",
  "Instagram",
  "Local Ad (Voice/Shopper)",
  "Search Engine",
  "Other"
];

const WHATS_INCLUDED = {
  exterior: ["Hand Washing", "Wheel & Tire Cleaning", "Waxing or Sealing", "Trim Restoration", "Window Cleaning"],
  interior: ["Vacuuming", "Carpet & Upholstery Cleaning", "Leather Cleaning & Conditioning", "Vinyl & Plastic Cleaning", "Window Cleaning", "Dashboard & Console Cleaning", "Detailing of Vents & Crevices"]
};

const BASIC_CLEANING_INCLUDED = {
  exterior: ["Hand Wash & Rinse", "Wheel Cleaning", "Tire Shine", "Window Cleaning", "Door Jamb Wipe Down"],
  interior: ["Vacuuming (seats, carpet, mats)", "Dashboard & Console Wipe", "Cup Holder Cleaning", "Air Freshener"]
};

// Booking window: March 15-31, 2026
const BOOKING_WINDOW_START = '2026-03-15';
const BOOKING_WINDOW_END = '2026-03-31';
const BOOKING_MIN_DATE = new Date(2026, 2, 15); // month is 0-indexed
const BOOKING_MAX_DATE = new Date(2026, 2, 31);

function isSaturday(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.getDay() === 6;
}

function isDateInWindow(dateStr) {
  return dateStr >= BOOKING_WINDOW_START && dateStr <= BOOKING_WINDOW_END;
}

function formatDateForDisplay(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function getPublicBookingDurationMinutes(vehicles) {
  return vehicles.reduce((sum, vehicle) => sum + (vehicle.serviceType === 'full_detailing' ? 120 : 60), 0);
}

function formatDurationLabel(minutes) {
  if (!minutes) return '0h';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

export default function CleaningBookingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get('type') || '';
  const allowedPublicServiceTypes = new Set(['residential', 'full_detailing']);
  const [currentStep, setCurrentStep] = useState(1);
  const [justCompletedStep, setJustCompletedStep] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [services, setServices] = useState(null);
  
  const [showHolidayPopup, setShowHolidayPopup] = useState(true);

  const [serviceCategory, setServiceCategory] = useState('residential');
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [vehicleYears, setVehicleYears] = useState([]);
  const [vehicleColors, setVehicleColors] = useState([]);

  const [selectedServiceType, setSelectedServiceType] = useState(
    allowedPublicServiceTypes.has(requestedType) ? requestedType : ''
  );
  const [selectedVehicleSize, setSelectedVehicleSize] = useState('');
  
  // Multiple vehicles support
  const [vehicles, setVehicles] = useState([]);
  
  const [customerInfo, setCustomerInfo] = useState({ full_name: '', phone: '', email: '' });
  const [serviceAddress, setServiceAddress] = useState({ street: '', city: '', state: 'NJ', zip_code: '', lat: null, lng: null });
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [notes, setNotes] = useState('');
  const [corporateId, setCorporateId] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');

  const [allowedDates, setAllowedDates] = useState(null);
  const [zone, setZone] = useState('');
  const [checkingZone, setCheckingZone] = useState(false);

  // Determine service type from added vehicles (Priority: Full Detailing > Residential)
  const hasFullDetail = vehicles.some(v => v.serviceType === 'full_detailing');
  const bookingServiceType = hasFullDetail ? 'full_detailing' : (vehicles[0]?.serviceType || 'residential');

  // Check Zone on Address Change (Residential Only)
  useEffect(() => {
    if (bookingServiceType !== 'residential') {
        setAllowedDates(null); // No restrictions for non-residential
        return;
    }

    if (serviceAddress.street && serviceAddress.city && serviceAddress.zip_code) {
      setCheckingZone(true);
      cleaningApi.checkZone(serviceAddress.street, serviceAddress.city, serviceAddress.zip_code, serviceAddress.lat, serviceAddress.lng)
        .then(res => {
           setAllowedDates(res.data.allowed_dates || []);
           setZone(res.data.zone);
           // If current selected date is not allowed, clear it
           if (preferredDate && res.data.allowed_dates && !res.data.allowed_dates.includes(preferredDate)) {
               setPreferredDate('');
               setPreferredTime('');
               toast.info(`Date reset: Your zone (${res.data.zone}) has specific service days.`);
           }
        })
        .catch((err) => {
           console.error(err);
           setAllowedDates([]); 
        })
        .finally(() => setCheckingZone(false));
    } else {
        setAllowedDates([]); // No address = no dates allowed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceAddress.street, serviceAddress.city, serviceAddress.zip_code, bookingServiceType, preferredDate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [svcRes, makesRes, yearsRes, colorsRes] = await Promise.all([
          cleaningApi.getServices(),
          getVehicleMakes(),
          getVehicleYears(),
          getVehicleColors()
        ]);
        setServices(svcRes.data.services);
        setVehicleMakes(makesRes.data.makes || []);
        setVehicleYears(yearsRes.data.years || []);
        setVehicleColors(colorsRes.data.colors || []);
      } catch (err) {
        toast.error('Failed to load data');
      }
    };
    load();
  }, []);

  // Fetch availability when date or service type changes
  useEffect(() => {
    if (!preferredDate) {
      setAvailableSlots([]);
      setAvailabilityMessage('');
      return;
    }
    // Validate date restrictions client-side first
    if (!isDateInWindow(preferredDate)) {
      setAvailableSlots([]);
      setAvailabilityMessage('Only dates from March 15-31 are available for booking.');
      setPreferredTime('');
      return;
    }
    if (isSaturday(preferredDate)) {
      setAvailableSlots([]);
      setAvailabilityMessage('Saturdays are not available for booking.');
      setPreferredTime('');
      return;
    }

    const numVehicles = Math.max(vehicles.length, 1);
    setLoadingSlots(true);
    setAvailabilityMessage('');
    cleaningApi.getAvailability(preferredDate, numVehicles, false, bookingServiceType, serviceAddress.zip_code, serviceAddress.street, serviceAddress.city, serviceAddress.lat, serviceAddress.lng, serviceCategory)
      .then(res => {
        const slots = res.data.slots || [];
        setAvailableSlots(slots);
        if (slots.length === 0) {
          setAvailabilityMessage(res.data.message || 'No available slots for this date.');
        }
        // Reset time if currently selected slot is unavailable
        if (preferredTime) {
          const match = slots.find(s => s.time === preferredTime);
          if (!match || !match.available) {
            setPreferredTime('');
          }
        }
      })
      .catch(() => {
        setAvailableSlots([]);
        setAvailabilityMessage('Could not load availability.');
      })
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredDate, vehicles.length, bookingServiceType, serviceAddress.zip_code, serviceAddress.street, serviceAddress.city, serviceCategory]); 

  const selectedPrice = services && selectedServiceType && selectedVehicleSize
    ? services[selectedServiceType]?.sizes[selectedVehicleSize]?.price
    : null;
  
  // Calculate total price for all vehicles
  const calculateTotalPrice = () => {
    return vehicles.reduce((total, v) => {
      const price = services?.[v.serviceType]?.sizes[v.vehicleSize]?.price || 0;
      return total + price;
    }, 0);
  };
  
  const totalPrice = calculateTotalPrice();
  const estimatedBookingDurationMinutes = getPublicBookingDurationMinutes(vehicles);

  // Add vehicle to list
  const addVehicle = () => {
    if (!selectedServiceType || !selectedVehicleSize) {
      toast.error('Please select service type and vehicle size');
      return;
    }

    if (vehicles.length > 0 && vehicles[0].serviceType !== selectedServiceType) {
      toast.error('All vehicles in one booking must use the same booking type');
      return;
    }
    
    const newVehicle = {
      id: Date.now(),
      serviceType: selectedServiceType,
      serviceName: services[selectedServiceType]?.name,
      vehicleSize: selectedVehicleSize,
      vehicleSizeLabel: services[selectedServiceType]?.sizes[selectedVehicleSize]?.label,
      price: services[selectedServiceType]?.sizes[selectedVehicleSize]?.price,
      vehicleInfo: { year: '', make: '', model: '', color: '' }
    };
    
    setVehicles(prev => [...prev, newVehicle]);
    setSelectedVehicleSize('');
    toast.success('Vehicle added to booking');
  };
  
  // Remove vehicle from list
  const removeVehicle = (id) => {
    setVehicles(prev => prev.filter(v => v.id !== id));
    toast.success('Vehicle removed');
  };
  
  // Update vehicle info for a specific vehicle
  const updateVehicleInfo = (id, field, value) => {
    setVehicles(prev => prev.map(v => 
      v.id === id ? { ...v, vehicleInfo: { ...v.vehicleInfo, [field]: value } } : v
    ));
  };

  const isValidUSPhone = (phone) => { const d = phone.replace(/\D/g, ''); return d.length === 10 || (d.length === 11 && d.startsWith('1')); };
  const isValidEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const validateStep = () => {
    switch (currentStep) {
      case 1: // Service
        if (vehicles.length === 0) { 
          toast.error('Please add at least one vehicle to your booking'); 
          return false; 
        }
        return true;
      case 2: // Details & Vehicle
        // Check customer info
        if (!customerInfo.full_name || !customerInfo.phone || !customerInfo.email) { toast.error('Please fill in all customer details'); return false; }
        if (!isValidUSPhone(customerInfo.phone)) { toast.error('Please enter a valid US phone number'); return false; }
        if (!isValidEmail(customerInfo.email)) { toast.error('Please enter a valid email'); return false; }
        
        // Check all vehicles have complete info
        for (let i = 0; i < vehicles.length; i++) {
          const v = vehicles[i].vehicleInfo;
          if (!v.year || !v.make || !v.model || !v.color) {
            toast.error(`Please fill in all info for Vehicle ${i + 1}`);
            return false;
          }
        }
        return true;
      case 3: // Location
        if (!serviceAddress.street || !serviceAddress.city || !serviceAddress.state || !serviceAddress.zip_code) { toast.error('Please fill in the full address'); return false; }
        return true;
      case 4: // Schedule
        if (!preferredDate) { toast.error('Please select a preferred date'); return false; }
        if (!preferredTime) { toast.error('Please select a preferred time'); return false; }
        return true;
      case 5: return true; // Review
      case 6: // Payment
        if (!referralSource) { toast.error('Please select how you heard about us'); return false; }
        return true;
      default: return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setJustCompletedStep(currentStep);
      setCurrentStep(prev => Math.min(prev + 1, 6));
      setTimeout(() => setJustCompletedStep(null), 800);
    }
  };
  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Create booking with multiple vehicles
      const bookingRes = await cleaningApi.createBooking({
        service_category: serviceCategory,
        service_type: vehicles[0].serviceType,
        vehicle_size: vehicles[0].vehicleSize,
        customer: customerInfo,
        vehicle: vehicles[0].vehicleInfo,
        service_address: serviceAddress,
        preferred_date: preferredDate,
        preferred_time: preferredTime,
        notes,
        // Additional vehicles data
        additional_vehicles: vehicles.slice(1).map(v => ({
          service_type: v.serviceType,
          vehicle_size: v.vehicleSize,
          vehicle: v.vehicleInfo
        })),
        total_vehicles: vehicles.length,
        total_price: totalPrice,
        referral_source: referralSource,
        corporate_id: isCorporateBooking ? normalizedCorporateId : undefined
      });

      if (isCorporateBooking) {
        navigate(`/cleaning/booking/success?booking_id=${bookingRes.data.id}&payment_status=corporate_billing`);
        return;
      }

      const checkoutRes = await cleaningApi.createCheckout(bookingRes.data.id, window.location.origin);
      if (checkoutRes.data.url) {
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) {
      console.error('Booking Error:', err);
      let msg = 'Unable to process booking.';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'object' ? JSON.stringify(detail) : detail;
      } else if (err.message) {
        msg = `Error: ${err.message}`;
        if (err.message === 'Network Error') {
            msg += ' (Check connection/CORS)';
        }
      } else {
        msg += ' (Unknown Error)';
      }
      
      toast.error(msg, { duration: 6000 });
      setIsLoading(false);
    }
  };

  const totalWithTax = totalPrice ? (totalPrice * 1.06625).toFixed(2) : '0.00';
  const normalizedCorporateId = corporateId.trim();
  const isCorporateBooking = bookingServiceType === 'residential' && AUTHORIZED_CORPORATE_IDS.has(normalizedCorporateId);
  const showCorporateIdError = normalizedCorporateId.length > 0 && !isCorporateBooking;

  return (
    <div className="min-h-screen bg-slate-50">
      {showHolidayPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white p-6 shadow-2xl animate-slide-up sm:p-8">
            <button
              onClick={() => setShowHolidayPopup(false)}
              className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
            </div>
            <h3 className="mb-3 text-center text-xl font-bold text-slate-900 font-['Outfit']">Notice</h3>
            <p className="mb-6 text-center text-sm leading-relaxed text-slate-600">
              Due to the upcoming Passover holiday, and to ensure everything is handled accurately, we’re currently accepting bookings by phone, text, or WhatsApp only. We appreciate your understanding and apologize for any inconvenience.
            </p>
            <div className="mb-6 rounded-xl bg-slate-50 p-4 text-center border border-slate-200">
              <a href="tel:732-732-4310" className="text-2xl font-bold text-emerald-600 hover:text-emerald-700">732-732-4310</a>
            </div>
            <Button
              onClick={() => setShowHolidayPopup(false)}
              className="w-full bg-emerald-500 py-3 text-base font-semibold text-white hover:bg-emerald-600 rounded-xl"
            >
              I Understand
            </Button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="cleaning-logo-link">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-xl text-slate-900 font-['Outfit']">Platinum Touch</span>
            </Link>
            <Link to="/cleaning" className="flex items-center gap-2 text-slate-600 hover:text-emerald-500 transition-colors" data-testid="back-cleaning-link">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      currentStep >= step.id ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'
                    } ${justCompletedStep === step.id ? 'animate-step-complete' : ''}`}
                    style={justCompletedStep === step.id ? {'--complete-color': '#22c55e'} : undefined}
                    data-testid={`cleaning-step-${step.id}`}
                  >
                    {currentStep > step.id ? (
                      <span className={justCompletedStep === step.id ? 'animate-check-draw' : ''}><Check className="w-5 h-5" /></span>
                    ) : <step.icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'}`}>{step.name}</span>
                </div>
                {index < STEPS.length - 1 && (() => {
                  let lineClass = 'bg-slate-200';
                  if (currentStep > step.id) {
                    lineClass = justCompletedStep === step.id ? 'bg-slate-200 animate-line-fill' : 'bg-emerald-500';
                  }
                  return (
                    <div className={`flex-1 h-0.5 mx-1 sm:mx-2 transition-colors duration-300 ${lineClass}`}
                         style={justCompletedStep === step.id ? {'--line-color': '#22c55e'} : undefined} />
                  );
                })()}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`max-w-3xl mx-auto px-4 py-8 ${currentStep < 6 ? 'pb-36 md:pb-8' : ''}`}>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          
          {/* Step 1: Service */}
          {currentStep === 1 && (
            <>
              <div className="mb-8 flex justify-center animate-fade-in-up">
                 <div className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 shadow-sm ring-1 ring-emerald-200" data-testid="public-booking-service-scope-pill">
                    <span>Residential bookings</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-emerald-300 bg-white text-xs font-bold text-emerald-700 transition hover:bg-emerald-100"
                          aria-label="Residential bookings information"
                          data-testid="public-booking-service-info-button"
                        >
                          i
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="center" sideOffset={10} className="w-80 rounded-2xl border-emerald-200 bg-white p-4 shadow-xl" data-testid="public-booking-service-info-popover">
                        <div className="space-y-3">
                          <p className="text-sm font-semibold text-slate-900">Need corporate or commercial help?</p>
                          <p className="text-sm leading-6 text-slate-600">
                            For corporate bookings or commercial jobs please reach out to us at{' '}
                            <a href="tel:+16822532633" className="font-semibold text-emerald-700 underline underline-offset-2" data-testid="public-booking-info-phone-link">
                              (682)-253-2633
                            </a>{' '}
                            or{' '}
                            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="font-semibold text-emerald-700 underline underline-offset-2" data-testid="public-booking-info-whatsapp-link">
                              WhatsApp us
                            </a>.
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                 </div>
              </div>

              <ServiceStep 
                services={services ? Object.fromEntries(Object.entries(services).filter(([k, v]) => (v.category || 'residential') === serviceCategory)) : null}
                selectedServiceType={selectedServiceType}
                setSelectedServiceType={setSelectedServiceType}
                selectedVehicleSize={selectedVehicleSize}
                setSelectedVehicleSize={setSelectedVehicleSize}
                WHATS_INCLUDED={WHATS_INCLUDED}
                BASIC_CLEANING_INCLUDED={BASIC_CLEANING_INCLUDED}
              />
              {/* Add Vehicle Button for Step 1 */}
              {selectedServiceType && selectedVehicleSize && (
                <div className="mt-6">
                  <Button
                    onClick={addVehicle}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl"
                    data-testid="add-vehicle-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Vehicle to Booking
                  </Button>
                </div>
              )}
              {/* Added Vehicles List in Step 1 */}
              {vehicles.length > 0 && (
                <div className="mt-6 border-t border-slate-200 pt-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Car className="w-5 h-5 text-emerald-500" />
                    Vehicles in Your Booking ({vehicles.length})
                  </h3>
                  <div className="space-y-3">
                    {vehicles.map((v, idx) => {
                      const vehicleIcons = {
                        'sedan': '/vehicle-sedan.png',
                        '2_row_suv': '/vehicle-2row-suv.png',
                        '3_row_suv': '/vehicle-3row-suv.png',
                        'minivan': '/vehicle-minivan.png'
                      };
                      return (
                        <div 
                          key={v.id} 
                          className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl p-4"
                        >
                          <div className="flex items-center gap-4">
                            <img 
                              src={vehicleIcons[v.vehicleSize]} 
                              alt={v.vehicleSizeLabel} 
                              className="w-12 h-12 object-contain"
                            />
                            <div>
                              <p className="font-semibold text-slate-900">Vehicle {idx + 1}: {v.vehicleSizeLabel}</p>
                              <p className="text-sm text-slate-500">{v.serviceName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <p className="font-bold text-emerald-600 text-lg">${v.price}</p>
                            <button
                              onClick={() => removeVehicle(v.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              data-testid={`remove-vehicle-${idx}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Total */}
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center bg-slate-900 text-white rounded-xl p-4">
                      <span className="font-semibold">Total ({vehicles.length} vehicle{vehicles.length > 1 ? 's' : ''})</span>
                      <span className="text-2xl font-bold">${totalPrice}</span>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="public-booking-duration-summary">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="font-semibold text-slate-800">Estimated booking duration</span>
                        <span className="font-bold text-emerald-700">{formatDurationLabel(estimatedBookingDurationMinutes)}</span>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500">
                        {vehicles[0]?.serviceType === 'full_detailing'
                          ? `${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} × 2 hours each`
                          : `${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} × 1 hour each`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 2: Details & Vehicle */}
          {currentStep === 2 && (
            <DetailsStep 
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              vehicles={vehicles}
              vehicleYears={vehicleYears}
              vehicleMakes={vehicleMakes}
              vehicleColors={vehicleColors}
              updateVehicleInfo={updateVehicleInfo}
            />
          )}

          {/* Step 3: Location */}
          {currentStep === 3 && (
            <LocationStep 
              serviceAddress={serviceAddress}
              setServiceAddress={setServiceAddress}
            />
          )}

          {/* Step 4: Schedule */}
          {currentStep === 4 && (
             <ScheduleStep 
              preferredDate={preferredDate}
              setPreferredDate={setPreferredDate}
              preferredTime={preferredTime}
              setPreferredTime={setPreferredTime}
              notes={notes}
              setNotes={setNotes}
              checkingZone={checkingZone}
              zone={zone}
              bookingServiceType={bookingServiceType}
              allowedDates={allowedDates}
              availabilityMessage={availabilityMessage}
              loadingSlots={loadingSlots}
              availableSlots={availableSlots}
              BOOKING_MIN_DATE={BOOKING_MIN_DATE}
              BOOKING_MAX_DATE={BOOKING_MAX_DATE}
              formatDateForDisplay={formatDateForDisplay}
            />
          )}

          {/* Step 5: Review */}
          {currentStep === 5 && (
            <ReviewStep 
              vehicles={vehicles}
              totalPrice={totalPrice}
              totalWithTax={totalWithTax}
              customerInfo={customerInfo}
              serviceAddress={serviceAddress}
              preferredDate={preferredDate}
              preferredTime={preferredTime}
              notes={notes}
            />
          )}

          {/* Step 6: Payment */}
          {currentStep === 6 && (
            <PaymentStep 
              referralSource={referralSource}
              setReferralSource={setReferralSource}
              totalWithTax={totalWithTax}
              REFERRAL_OPTIONS={REFERRAL_OPTIONS}
            />
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200">
            {currentStep > 1 ? (
              <Button variant="outline" onClick={handleBack} className="px-6" data-testid="cleaning-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            ) : <div />}
            
            {currentStep < 6 ? (
              <Button onClick={handleNext} className="hidden bg-emerald-500 px-8 text-white hover:bg-emerald-600 md:inline-flex" data-testid="cleaning-next-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-start">
                <div className="w-full md:w-[320px]">
                  <label htmlFor="corporate-id-input" className="mb-1 block text-sm font-medium text-slate-700">
                    Corporate ID
                  </label>
                  <Input
                    id="corporate-id-input"
                    type="text"
                    inputMode="numeric"
                    value={corporateId}
                    onChange={(e) => setCorporateId(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter ID for direct corporate billing"
                    className={`h-11 ${showCorporateIdError ? 'border-red-300 focus-visible:ring-red-400' : ''}`}
                    data-testid="cleaning-corporate-id-input"
                  />
                  {showCorporateIdError && (
                    <p className="mt-1 text-xs font-medium text-red-600" data-testid="cleaning-corporate-id-error">
                      Invalid Corporate ID. Please use a credit card or contact your administrator.
                    </p>
                  )}
                </div>
                <Button onClick={handleSubmit} disabled={isLoading} className="bg-emerald-500 px-8 text-white hover:bg-emerald-600 md:mt-6" data-testid="cleaning-pay-btn">
                  {(() => {
                    if (isLoading) return 'Processing...';
                    if (isCorporateBooking) return 'Submit Corporate Booking';
                    return `Pay $${totalWithTax}`;
                  })()}
                  <CreditCard className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {currentStep < 6 && (
        <MobileBookingActionBar
          onClick={handleNext}
          buttonClassName="bg-emerald-500 text-white hover:bg-emerald-600"
          dataTestId="cleaning-mobile-next-btn"
        >
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </MobileBookingActionBar>
      )}

      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed right-4 z-50 inline-flex items-center gap-2 rounded-full border-2 border-white/40 bg-[#25D366] px-5 py-4 text-white shadow-[0_18px_45px_rgba(37,211,102,0.45)] ring-4 ring-[#25D366]/20 transition-all duration-300 hover:scale-105 hover:bg-[#128C7E] sm:right-6 ${currentStep < 6 ? 'bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] sm:bottom-6' : 'bottom-6'}`}
        data-testid="cleaning-booking-whatsapp-button"
      >
        <MessageCircle className="h-6 w-6" />
        <span className="text-sm font-semibold tracking-tight">Need help?</span>
      </a>
    </div>
  );
}
